import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Modal, Form, Input, Select, Switch, DatePicker, Tag, Popconfirm, message, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  getAllMaintenanceNotices,
  createMaintenanceNotice,
  updateMaintenanceNotice,
  deleteMaintenanceNotice,
} from "../../../services/MaintenanceService";

const { TextArea } = Input;
const { Option } = Select;

const SEVERITY_COLORS = {
  info: { color: "#1a73e8", bg: "#e8f0fe", label: "Info" },
  warning: { color: "#d97706", bg: "#fef3c7", label: "Warning" },
  critical: { color: "#dc2626", bg: "#fee2e2", label: "Critical" },
};

const SeverityTag = ({ severity }) => {
  const cfg = SEVERITY_COLORS[severity] || SEVERITY_COLORS.info;
  return (
    <Tag
      style={{
        color: cfg.color,
        background: cfg.bg,
        border: `1px solid ${cfg.color}`,
        fontWeight: 600,
        borderRadius: 4,
      }}
    >
      {cfg.label}
    </Tag>
  );
};

const formatDateTime = (dt) => (dt ? dayjs(dt).format("DD MMM YYYY, HH:mm") : "—");

const MaintenanceManager = () => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null); // null = create mode
  const [submitting, setSubmitting] = useState(false);

  const [form] = Form.useForm();

  // ── Data fetching — hooks must always run before any early return ──────────
  const fetchNotices = useCallback(async () => {
    if (role !== "superadmin") return;
    setLoading(true);
    try {
      const res = await getAllMaintenanceNotices(token);
      setNotices(res?.data || []);
    } catch (err) {
      message.error("Failed to load maintenance notices");
    } finally {
      setLoading(false);
    }
  }, [token, role]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // ── Guard: superadmin only (after all hooks) ───────────────────────────────
  if (role !== "superadmin") {
    return (
      <div style={styles.unauthorizedWrap}>
        <div style={styles.unauthorizedBox}>
          <span style={{ fontSize: 32 }}>🔒</span>
          <h3 style={{ margin: "12px 0 6px" }}>Access Restricted</h3>
          <p style={{ color: "#6b7280", margin: 0 }}>This page is only accessible to superadmins.</p>
        </div>
      </div>
    );
  }

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingNotice(null);
    form.resetFields();
    form.setFieldsValue({ severity: "info", is_active: true, is_dismissible: true });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingNotice(record);
    form.setFieldsValue({
      title: record.title,
      message: record.message,
      severity: record.severity,
      is_active: record.is_active,
      is_dismissible: record.is_dismissible,
      start_time: record.start_time ? dayjs(record.start_time) : null,
      end_time: record.end_time ? dayjs(record.end_time) : null,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingNotice(null);
    form.resetFields();
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        title: values.title,
        message: values.message,
        severity: values.severity,
        is_active: values.is_active ?? true,
        is_dismissible: values.is_dismissible ?? true,
        start_time: values.start_time ? values.start_time.toISOString() : null,
        end_time: values.end_time ? values.end_time.toISOString() : null,
      };

      if (editingNotice) {
        await updateMaintenanceNotice(token, editingNotice.id, payload);
        message.success("Notice updated successfully");
      } else {
        await createMaintenanceNotice(token, payload);
        message.success("Notice created successfully");
      }
      closeModal();
      fetchNotices();
    } catch (err) {
      message.error(err?.response?.data?.detail || "Failed to save notice");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Quick toggle active ────────────────────────────────────────────────────
  const handleToggleActive = async (record) => {
    try {
      await updateMaintenanceNotice(token, record.id, { is_active: !record.is_active });
      message.success(`Notice ${record.is_active ? "deactivated" : "activated"}`);
      fetchNotices();
    } catch {
      message.error("Failed to toggle notice status");
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await deleteMaintenanceNotice(token, id);
      message.success("Notice deleted");
      fetchNotices();
    } catch {
      message.error("Failed to delete notice");
    }
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      render: (text, record) => (
        <span style={{ fontWeight: 600, color: record.is_active ? "#1f2937" : "#6b7280" }}>
          {text}
        </span>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      width: 300,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ color: "#374151", fontSize: 13 }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 110,
      render: (s) => <SeverityTag severity={s} />,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (active) =>
        active ? (
          <Tag color="green" style={{ borderRadius: 4 }}>Active</Tag>
        ) : (
          <Tag color="default" style={{ borderRadius: 4, color: "#9ca3af" }}>Inactive</Tag>
        ),
    },
    {
      title: "Dismissible",
      dataIndex: "is_dismissible",
      key: "is_dismissible",
      width: 110,
      render: (val) => (
        <span style={{ color: val ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
          {val ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      key: "start_time",
      width: 160,
      render: (dt) => (
        <span style={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
          {dt ? formatDateTime(dt) : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Immediate</span>}
        </span>
      ),
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      key: "end_time",
      width: 160,
      render: (dt) => (
        <span style={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
          {dt ? formatDateTime(dt) : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>No expiry</span>}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
              style={styles.actionBtn}
            />
          </Tooltip>
          <Tooltip title={record.is_active ? "Deactivate" : "Activate"}>
            <Button
              size="small"
              icon={<PoweroffOutlined />}
              onClick={() => handleToggleActive(record)}
              style={{
                ...styles.actionBtn,
                color: record.is_active ? "#d97706" : "#16a34a",
                borderColor: record.is_active ? "#fde68a" : "#bbf7d0",
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this notice?"
              description="This action cannot be undone. Consider deactivating instead."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              placement="left"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ ...styles.actionBtn, borderColor: "#fca5a5" }}
              />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.heading}>Maintenance Notices</h2>
          <p style={styles.subheading}>
            Configure system-wide banners shown to all logged-in users. Active notices appear as a
            top banner on every page.
          </p>
        </div>
        <Button
          type="primary"
          className="cip-btn-submit"
          icon={<PlusOutlined />}
          onClick={openCreate}
          style={styles.createBtn}
          id="btn-create-maintenance-notice"
        >
          New Notice
        </Button>
      </div>

      {/* Preview strip: active notices at a glance */}
      {notices.filter((n) => n.is_active).length > 0 && (
        <div style={styles.activeStrip}>
          <span style={{ fontWeight: 600, marginRight: 8, fontSize: 12 }}>CURRENTLY ACTIVE:</span>
          {notices
            .filter((n) => n.is_active)
            .map((n) => (
              <Tag
                key={n.id}
                style={{
                  background: SEVERITY_COLORS[n.severity]?.bg,
                  color: SEVERITY_COLORS[n.severity]?.color,
                  border: `1px solid ${SEVERITY_COLORS[n.severity]?.color}`,
                  borderRadius: 4,
                  fontWeight: 500,
                }}
              >
                {SEVERITY_COLORS[n.severity]?.label?.toUpperCase()} · {n.title}
              </Tag>
            ))}
        </div>
      )}

      {/* Table */}
      <div id="maintenance-manager-table" style={styles.tableWrap}>
        <Table
          dataSource={notices}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="middle"
          rowClassName={(record) => (!record.is_active ? "mn-row-inactive" : "")}
          style={{ borderRadius: 8, overflow: "hidden" }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            {editingNotice ? "Edit Maintenance Notice" : "Create Maintenance Notice"}
          </span>
        }
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 12 }}
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: "Title is required" }, { max: 255 }]}
          >
            <Input
              id="input-notice-title"
              placeholder='e.g. "Scheduled Maintenance – 11 PM to 1 AM IST"'
              maxLength={255}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: "Message is required" }]}
          >
            <TextArea
              id="input-notice-message"
              placeholder="Describe what users should expect during the maintenance window…"
              autoSize={{ minRows: 3, maxRows: 6 }}
              showCount
            />
          </Form.Item>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item label="Severity" name="severity" style={{ flex: 1 }} rules={[{ required: true }]}>
              <Select id="select-notice-severity">
                <Option value="info">
                  <span style={{ color: SEVERITY_COLORS.info.color }}>● Info</span>
                </Option>
                <Option value="warning">
                  <span style={{ color: SEVERITY_COLORS.warning.color }}>● Warning</span>
                </Option>
                <Option value="critical">
                  <span style={{ color: SEVERITY_COLORS.critical.color }}>● Critical</span>
                </Option>
              </Select>
            </Form.Item>

            <Form.Item label="Active" name="is_active" valuePropName="checked" style={{ flex: 0.5 }}>
              <Switch id="switch-notice-active" checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>

            <Form.Item label="Dismissible" name="is_dismissible" valuePropName="checked" style={{ flex: 0.5 }}>
              <Switch id="switch-notice-dismissible" checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <Form.Item
              label="Start Time"
              name="start_time"
              style={{ flex: 1 }}
              extra="Leave blank to activate immediately"
            >
              <DatePicker
                id="picker-notice-start"
                showTime
                format="DD MMM YYYY HH:mm"
                style={{ width: "100%" }}
                placeholder="Activate immediately"
              />
            </Form.Item>

            <Form.Item
              label="End Time"
              name="end_time"
              style={{ flex: 1 }}
              extra="Leave blank for no expiry"
            >
              <DatePicker
                id="picker-notice-end"
                showTime
                format="DD MMM YYYY HH:mm"
                style={{ width: "100%" }}
                placeholder="No automatic expiry"
              />
            </Form.Item>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
            <Button onClick={closeModal} id="btn-cancel-notice">Cancel</Button>
            <Button
              type="primary"
              className="cip-btn-submit"
              htmlType="submit"
              loading={submitting}
              id="btn-submit-notice"
            >
              {editingNotice ? "Save Changes" : "Create Notice"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Force table cell visibility regardless of app theme */}
      <style>{`
        /* Scoped to this page's table */
        #maintenance-manager-table .ant-table {
          background: #fff !important;
          color: #1f2937 !important;
        }
        #maintenance-manager-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #374151 !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          border-bottom: 2px solid #e5e7eb !important;
        }
        #maintenance-manager-table .ant-table-tbody > tr > td {
          background: #fff !important;
          color: #1f2937 !important;
          border-bottom: 1px solid #f1f5f9 !important;
        }
        #maintenance-manager-table .ant-table-tbody > tr:hover > td {
          background: #f8fafc !important;
        }
        #maintenance-manager-table .mn-row-inactive > td {
          background: #f9fafb !important;
          color: #9ca3af !important;
        }
        #maintenance-manager-table .ant-pagination {
          padding: 12px 16px !important;
          background: #fff !important;
        }
        #maintenance-manager-table .ant-table-wrapper {
          border-radius: 8px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  page: {
    padding: "28px 32px",
    maxWidth: 1100,
    margin: "0 auto",
    fontFamily: "'Inter', 'Roboto', sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 20,
    gap: 16,
  },
  heading: {
    fontSize: 22,
    fontWeight: 700,
    color: "#111827",
    margin: 0,
  },
  subheading: {
    fontSize: 13,
    color: "#6b7280",
    margin: "4px 0 0",
    maxWidth: 520,
  },
  createBtn: {
    borderRadius: 6,
    height: 36,
    fontWeight: 600,
    flexShrink: 0,
  },
  activeStrip: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    padding: "10px 16px",
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    fontSize: 12,
    color: "#16a34a",
  },
  tableWrap: {
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  actionBtn: {
    borderRadius: 4,
    color: "#374151",
    borderColor: "#e5e7eb",
  },
  unauthorizedWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
  },
  unauthorizedBox: {
    textAlign: "center",
    padding: 40,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  },
};

export default MaintenanceManager;
